/**
 * كتالوج التدريب والتطوير.
 *
 * احتياجٌ يُكتب فيُعرف من أين جاء، ودورةٌ في الكتالوج تُعقد مرّاتٍ، وقيدٌ
 * في انعقادٍ بعينه، ثمّ أثرٌ يُقاس بما يصحّ قياسُه.
 *
 * ## ولماذا الدورةُ غيرُ انعقادها
 *
 * «أساسيّات إدارة المشاريع» دورةٌ تُعقد ثلاثَ مرّاتٍ في السنة بثلاثة
 * مدرّبين وثلاث تكاليف وثلاثةِ حضور. وخلطُهما — كما تفعل أنظمةٌ كثيرة —
 * يجعل التكلفةَ والحضورَ والأثرَ غيرَ قابلةٍ للجمع، ويجعل «كم أنفقنا على
 * هذه الدورة» سؤالًا بلا جواب.
 *
 * ## وما لا يفعله هذا الباب
 *
 * لا يحمل محتوًى تعليميًّا ولا اختباراتٍ ولا فيديو: هو سجلُّ ما وقع لا
 * منصّةُ تعلُّم. والمحتوى عند مقدّمه، وما يُكتب هنا أنّ فلانًا حضر في يومٍ
 * بعينه وأخذ كذا ونالَ شهادةً برقمها.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   مصدرُ الاحتياج
   ========================================================================== */

export const NEED_SOURCES = [
  'REVIEW',
  'SUCCESSION',
  'MANAGER',
  'SELF',
  'COMPLIANCE',
] as const;
export type NeedSource = (typeof NEED_SOURCES)[number];

export interface NeedSourceDef extends Labelled<NeedSource> {
  description: string;
  /** هل جاء من قياسٍ مكتوبٍ في النظام أم من رأي. */
  measured: boolean;
}

/**
 * المصدرُ يُكتب لأنّه يغيّر قراءةَ الاحتياج.
 *
 * «يحتاج دورةَ إدارة وقت» من تقييمٍ أُرسل غيرُها من طلبِ صاحبها: الأولى
 * فجوةٌ قِيست، والثانية رغبةٌ تُقدَّر. وخلطُهما يجعل ميزانيّةَ التدريب
 * تُنفَق على ما يُطلَب لا على ما ينقص.
 */
export const NEED_SOURCE_DEFS: readonly NeedSourceDef[] = [
  {
    key: 'REVIEW',
    label: 'من تقييم أداء',
    kind: 'info',
    description: 'فجوةٌ ظهرت في تقييمٍ أُرسل.',
    measured: true,
  },
  {
    key: 'SUCCESSION',
    label: 'من خطّة إحلال',
    kind: 'info',
    description: 'ما ينقص مرشّحًا ليجهز للوظيفة التي يخلف فيها.',
    measured: true,
  },
  {
    key: 'MANAGER',
    label: 'كتبه مديرُه',
    kind: 'neutral',
    description: 'ملاحظةُ مديره — رأيٌ يُؤخذ به ولم يُقس.',
    measured: false,
  },
  {
    key: 'SELF',
    label: 'طلبه صاحبُه',
    kind: 'neutral',
    description: 'رغبةُ الموظّف نفسِه.',
    measured: false,
  },
  {
    key: 'COMPLIANCE',
    label: 'إلزامٌ خارجيّ',
    kind: 'warning',
    description: 'يفرضه نظامٌ أو جهة — سلامةٌ أو ترخيصٌ أو التزام.',
    measured: true,
  },
] as const;

export const NEED_SOURCE_MAP = Object.fromEntries(
  NEED_SOURCE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<NeedSource, NeedSourceDef>>;

/* ==========================================================================
   حالةُ الاحتياج
   ========================================================================== */

export const NEED_STATUSES = ['OPEN', 'PLANNED', 'MET', 'DROPPED'] as const;
export type NeedStatus = (typeof NEED_STATUSES)[number];

export interface NeedStatusDef extends Labelled<NeedStatus> {
  description: string;
  /** هل ما زال ينتظر عملًا. */
  live: boolean;
}

export const NEED_STATUS_DEFS: readonly NeedStatusDef[] = [
  {
    key: 'OPEN',
    label: 'مكتوب',
    kind: 'warning',
    description: 'عُرف ولم يُدرَج في دورةٍ بعد.',
    live: true,
  },
  {
    key: 'PLANNED',
    label: 'مُدرَج في دورة',
    kind: 'info',
    description: 'أُلحق صاحبُه بانعقادٍ لم ينته بعد.',
    live: true,
  },
  {
    key: 'MET',
    label: 'سُدّ',
    kind: 'success',
    description: 'حضر وأُغلق الاحتياج.',
    live: false,
  },
  {
    key: 'DROPPED',
    label: 'أُسقط',
    kind: 'neutral',
    description: 'تغيّر العملُ فلم يعد مطلوبًا.',
    live: false,
  },
] as const;

export const NEED_STATUS_MAP = Object.fromEntries(
  NEED_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<NeedStatus, NeedStatusDef>>;

/** الأولويّاتُ ثلاثٌ — وواحدٌ أعلاها. */
export const NEED_PRIORITIES = [1, 2, 3] as const;
export const NEED_PRIORITY_LABELS: Readonly<Record<number, string>> = {
  1: 'عاجل',
  2: 'مهمّ',
  3: 'متى أمكن',
};

/* ==========================================================================
   شكلُ التدريب ومسارُه
   ========================================================================== */

export const TRAINING_KINDS = ['INTERNAL', 'EXTERNAL', 'ONLINE'] as const;
export type TrainingKindKey = (typeof TRAINING_KINDS)[number];

export interface TrainingKindDef extends Labelled<TrainingKindKey> {
  description: string;
  /** هل تُتوقَّع لها جهةٌ مقدِّمة. */
  hasProvider: boolean;
}

export const TRAINING_KIND_DEFS: readonly TrainingKindDef[] = [
  {
    key: 'INTERNAL',
    label: 'داخليّة',
    kind: 'neutral',
    description: 'بمدرّبٍ من الشركة — تكلفتُها وقتُ من يدرّب.',
    hasProvider: false,
  },
  {
    key: 'EXTERNAL',
    label: 'خارجيّة',
    kind: 'info',
    description: 'عند جهةٍ خارجيّة — لها فاتورةٌ وتُقاس كلفتُها.',
    hasProvider: true,
  },
  {
    key: 'ONLINE',
    label: 'عن بُعد',
    kind: 'info',
    description: 'مسجَّلةٌ أو مباشِرة — والحضورُ فيها يُسجَّل بيد.',
    hasProvider: true,
  },
] as const;

export const TRAINING_KIND_MAP = Object.fromEntries(
  TRAINING_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<TrainingKindKey, TrainingKindDef>>;

export const TRAINING_TRACKS = [
  'GENERAL',
  'LEADERSHIP',
  'TECHNICAL',
  'COMPLIANCE',
  'ONBOARDING',
] as const;
export type TrainingTrack = (typeof TRAINING_TRACKS)[number];

export interface TrackDef extends Labelled<TrainingTrack> {
  description: string;
}

export const TRACK_DEFS: readonly TrackDef[] = [
  {
    key: 'GENERAL',
    label: 'عامّ',
    kind: 'neutral',
    description: 'مهاراتٌ يحتاجها الجميع.',
  },
  {
    key: 'LEADERSHIP',
    label: 'برامج القيادة',
    kind: 'success',
    description:
      'لمن يقود اليوم أو يُعدّ ليقود — ويُقرأ مع خطط الإحلال، فالمرشَّحُ الذي يحتاج سنةً هذا ما يحتاجه فيها.',
  },
  {
    key: 'TECHNICAL',
    label: 'تقنيّ ومهنيّ',
    kind: 'info',
    description: 'أدواتُ العمل نفسِه.',
  },
  {
    key: 'COMPLIANCE',
    label: 'التزامٌ وسلامة',
    kind: 'warning',
    description: 'ما يفرضه نظامٌ أو جهة — حضورُه ليس اختياريًّا.',
  },
  {
    key: 'ONBOARDING',
    label: 'تهيئة الملتحقين',
    kind: 'info',
    description: 'أوّلُ ما يأخذه من التحق حديثًا.',
  },
] as const;

export const TRACK_MAP = Object.fromEntries(
  TRACK_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<TrainingTrack, TrackDef>>;

/* ==========================================================================
   حالةُ الانعقاد
   ========================================================================== */

export const SESSION_STATUSES = [
  'PLANNED',
  'RUNNING',
  'DONE',
  'CANCELLED',
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export interface SessionStatusDef extends Labelled<SessionStatus> {
  description: string;
  /** هل يُقبل قيدُ متدرّبٍ جديدٍ فيه. */
  enrolling: boolean;
  /** هل تُكتب نتائجُه. */
  results: boolean;
}

export const SESSION_STATUS_DEFS: readonly SessionStatusDef[] = [
  {
    key: 'PLANNED',
    label: 'مجدولة',
    kind: 'info',
    description: 'لم تبدأ — تُقيَّد فيها المقاعد.',
    enrolling: true,
    results: false,
  },
  {
    key: 'RUNNING',
    label: 'جارية',
    kind: 'warning',
    description: 'بدأت — يُسجَّل فيها الحضور.',
    enrolling: true,
    results: true,
  },
  {
    key: 'DONE',
    label: 'انتهت',
    kind: 'success',
    description: 'انتهت وأُغلقت نتائجُها.',
    enrolling: false,
    results: false,
  },
  {
    key: 'CANCELLED',
    label: 'أُلغيت',
    kind: 'neutral',
    description: 'لم تنعقد — ومن قُيّد فيها يعود احتياجُه مكتوبًا.',
    enrolling: false,
    results: false,
  },
] as const;

export const SESSION_STATUS_MAP = Object.fromEntries(
  SESSION_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<SessionStatus, SessionStatusDef>>;

/* ==========================================================================
   حالةُ القيد
   ========================================================================== */

export const ENROLLMENT_STATUSES = [
  'ENROLLED',
  'ATTENDED',
  'ABSENT',
  'CANCELLED',
] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export interface EnrollmentStatusDef extends Labelled<EnrollmentStatus> {
  description: string;
  /** هل يشغل مقعدًا في العدّ. */
  holdsSeat: boolean;
  /** هل يُحسب في تكلفة المتدرّب الواحد. */
  costed: boolean;
}

/**
 * الغائبُ يبقى مقيَّدًا.
 *
 * حذفُ قيده يُخفي أنّ مقعدًا دُفع ثمنُه وأُهدر — وهو أهمُّ رقمٍ في تقرير
 * التدريب: «ربعُ من قُيّدوا لم يحضروا» يغيّر كيف تُحجز المقاعد.
 */
export const ENROLLMENT_STATUS_DEFS: readonly EnrollmentStatusDef[] = [
  {
    key: 'ENROLLED',
    label: 'مقيَّد',
    kind: 'info',
    description: 'له مقعدٌ ولم ينعقد بعد.',
    holdsSeat: true,
    costed: true,
  },
  {
    key: 'ATTENDED',
    label: 'حضر',
    kind: 'success',
    description: 'حضر — ومنه يُقاس الأثر.',
    holdsSeat: true,
    costed: true,
  },
  {
    key: 'ABSENT',
    label: 'تغيّب',
    kind: 'danger',
    description: 'قُيّد ولم يحضر — ومقعدُه دُفع ثمنُه وأُهدر.',
    holdsSeat: true,
    costed: true,
  },
  {
    key: 'CANCELLED',
    label: 'أُلغي قيدُه',
    kind: 'neutral',
    description: 'خرج قبل الانعقاد فأُفرج عن مقعده.',
    holdsSeat: false,
    costed: false,
  },
] as const;

export const ENROLLMENT_STATUS_MAP = Object.fromEntries(
  ENROLLMENT_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<EnrollmentStatus, EnrollmentStatusDef>>;

/* ==========================================================================
   المهارات
   ========================================================================== */

export const MAX_COURSE_SKILLS = 12;

/**
 * مهاراتُ الدورة نصوصٌ لا مفاتيح.
 *
 * كتالوجُ مهاراتٍ مغلقٌ في الشيفرة يمنع كلَّ مهارةٍ تخصّ صناعةَ الشركة،
 * وجدولُ مهاراتٍ للشركة بابٌ ثالثٌ يُملأ مرّةً ويُهمَل. والنصُّ يُقرأ
 * بالعين وهو ما يُقرأ عليه القرار.
 */
export function readSkills(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const text = item.trim();
    if (!text) continue;
    out.push(text);
    if (out.length >= MAX_COURSE_SKILLS) break;
  }
  return out;
}

/* ==========================================================================
   التكلفة والأثر
   ========================================================================== */

/**
 * تكلفةُ المتدرّب الواحد.
 *
 * تُقسَم على من شغل مقعدًا لا على من حضر: المتغيّبُ دُفع ثمنُ مقعده، وقسمةُ
 * التكلفة على الحاضرين وحدَهم تُخفي الهدرَ وتجعل الدورةَ تبدو أغلى ممّا
 * هي. وبلا تكلفةٍ مكتوبةٍ يُردّ فارغًا — لا صفرًا: صفرٌ يعني «مجّانيّة»
 * وهو خبرٌ لم يقله أحد.
 */
export function costPerSeat(cost: number | null, seats: number): number | null {
  if (cost === null || seats <= 0) return null;
  return Math.round((cost / seats) * 100) / 100;
}

/**
 * أقلُّ عددِ حاضرين يُنشر عنه متوسّطُ رأيٍ في دورة.
 *
 * رأيُ اثنين في مدرّبٍ يكشف صاحبَيه لمن يعرف من حضر، ويُقرأ حكمًا على
 * الدورة وهو رأيُ اثنين.
 */
export const MIN_FEEDBACK_SAMPLE = 4;

/** أعلى درجةٍ يُقيَّم بها المتدرّبُ الدورةَ. */
export const MAX_RATING = 5;

/**
 * أقلُّ عددِ مقارناتٍ يُنشر عنه أثرٌ على الأداء.
 *
 * والأثرُ هنا فارقُ محصّلةٍ بين تقييمٍ قبل الدورة وتقييمٍ بعدها. وهو
 * ارتباطٌ لا سببيّة: من حضر دورةً حضر معها سنةً كاملةً فيها عشرةُ أسبابٍ
 * أخرى. ويُقال ذلك في الشاشة صراحةً — ورقمٌ بلا هذه الجملة يُقرأ على أنّه
 * إثباتٌ لما لا يُثبَت.
 */
export const MIN_IMPACT_SAMPLE = 3;
